import { useState } from "react";
import { Link, useParams } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  AlertTriangle,
  Bell,
  FileDown,
  Truck,
  Scissors,
  Wrench,
  XCircle,
  FileText,
} from "lucide-react";
import type { LucideIcon } from "lucide-react";

import { ApiError, formatCLP, formatDateTime } from "../../lib/api";
import { fechaLarga } from "../../lib/format";
import { useToast } from "../../components/Toast";
import {
  Button,
  Cargando,
  ErrorCarga,
  Input,
  PageHeader,
} from "../../components/ui";
import {
  cortarOrden,
  descargarAvisoCorte,
  marcarEnRuta,
  notificarOrden,
  obtenerOrden,
  reponerOrden,
  anularOrden,
  type EstadoOrdenCorte,
  type OrdenCorteRead,
} from "./api";
import { EstadoCorteBadge } from "./estado";
import { fetchContratos } from "../socios/api";
import { fetchEmpresaConfiguracion } from "../empresa/api";
import { deudaDelContrato } from "../contratos/api";
import { hoyLocalISO } from "../../lib/fechaLocal";


const VOLVER_CORTES = { etiqueta: "Cortes", ruta: "/cortes" };

type AccionPanel =
  | { kind: "Notificar" }
  | { kind: "EnRuta" }
  | { kind: "Cortar" }
  | { kind: "Reponer" }
  | { kind: "Anular" };

const ACCIONES_POR_ESTADO: Partial<Record<EstadoOrdenCorte, AccionPanel[]>> = {
  Pendiente: [{ kind: "Notificar" }, { kind: "Anular" }],
  Notificada: [{ kind: "EnRuta" }, { kind: "Anular" }],
  EnRuta: [{ kind: "Cortar" }, { kind: "Anular" }],
  Cortada: [{ kind: "Reponer" }, { kind: "Anular" }],
};

const ETIQUETA_ACCION: Record<AccionPanel["kind"], string> = {
  Notificar: "Notificar",
  EnRuta: "Marcar en ruta",
  Cortar: "Cortar",
  Reponer: "Reponer",
  Anular: "Anular orden",
};

/** Qué sigue en cada estado, en una frase para la oficina. */
const EXPLICACION_POR_ESTADO: Partial<Record<EstadoOrdenCorte, string>> = {
  Pendiente:
    "Avisa al socio antes de cortar: notifícalo y entrégale el aviso impreso.",
  Notificada: "Cuando el operador salga a cortar, márcala en ruta.",
  EnRuta: "Registra el corte cuando se ejecute.",
  Cortada: "Cuando pague, repón el servicio.",
};

const ICONS: Record<AccionPanel["kind"], LucideIcon> = {
  Notificar: Bell,
  EnRuta: Truck,
  Cortar: Scissors,
  Reponer: Wrench,
  Anular: XCircle,
};

export function CorteDetailPage(): JSX.Element {
  const { ordenId: ordenIdParam } = useParams<{ ordenId: string }>();
  const ordenId = Number(ordenIdParam);

  const ordenQ = useQuery({
    queryKey: ["orden-corte", ordenId],
    queryFn: () => obtenerOrden(ordenId),
    enabled: Number.isFinite(ordenId),
  });

  const contratosQ = useQuery({
    queryKey: ["contratos"],
    queryFn: fetchContratos,
  });

  if (!Number.isFinite(ordenId)) {
    return <p className="text-sm text-rose-700">Orden inválida.</p>;
  }

  const contrato = ordenQ.data
    ? contratosQ.data?.find((c) => c.id === ordenQ.data!.contrato_id)
    : undefined;

  return (
    <div className="space-y-6">
      {!ordenQ.data && (
        <PageHeader title={`Orden de corte #${ordenId}`} volver={VOLVER_CORTES} />
      )}
      {ordenQ.isPending && <Cargando />}
      {ordenQ.isError && (
        <ErrorCarga
          error={ordenQ.error}
          mensaje="No se pudo cargar la orden."
          onReintentar={() => void ordenQ.refetch()}
        />
      )}

      {ordenQ.data && (
        <OrdenDetalle
          orden={ordenQ.data}
          contratoNombre={contrato?.socio_nombre ?? null}
          contratoSocioId={contrato?.socio_id ?? null}
          contratoArranque={
            contrato
              ? `${contrato.arranque_codigo ?? "—"} · ${contrato.arranque_direccion}`
              : null
          }
        />
      )}
    </div>
  );
}

interface DetalleProps {
  orden: OrdenCorteRead;
  contratoNombre: string | null;
  contratoSocioId: number | null;
  contratoArranque: string | null;
}

function OrdenDetalle({
  orden,
  contratoNombre,
  contratoSocioId,
  contratoArranque,
}: DetalleProps): JSX.Element {
  const acciones = ACCIONES_POR_ESTADO[orden.estado] ?? [];

  return (
    <>
      <PageHeader
        title={`Orden de corte #${orden.id}`}
        subtitle={
          <>
            {contratoNombre ?? `Contrato #${orden.contrato_id}`}
            {contratoArranque && <> · {contratoArranque}</>}
          </>
        }
        volver={VOLVER_CORTES}
        actions={
          <div className="flex flex-col items-end gap-2 text-right">
            <EstadoCorteBadge estado={orden.estado} />
            <div className="text-xs text-neutral-600">
              Generada {formatDateTime(orden.fecha_generacion)}
            </div>
            {orden.fecha_corte_programada && (
              <div className="text-xs text-neutral-600">
                Programada para {fechaLarga(orden.fecha_corte_programada)}
              </div>
            )}
            {orden.estado !== "Anulada" && <DescargarAviso orden={orden} />}
          </div>
        }
      />
      {contratoSocioId !== null && (
        <Link
          to={`/socios/${contratoSocioId}/cuenta-corriente?contrato_id=${orden.contrato_id}`}
          className="-mt-3 inline-flex items-center gap-1 text-xs font-medium text-brand-700 hover:text-brand-800"
        >
          <FileText className="h-3 w-3" aria-hidden="true" />
          Ver cuenta corriente del socio
        </Link>
      )}

      {orden.motivo_anulacion && (
        <div className="rounded-lg border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-800">
          <strong>Motivo anulación:</strong> {orden.motivo_anulacion}
        </div>
      )}

      <PanelAcciones orden={orden} acciones={acciones} />

      <section className="grid grid-cols-1 md:grid-cols-3 gap-3">
        <Card label="Deuda al corte" value={formatCLP(orden.monto_deuda_al_corte)} />
        <Card
          label="Costo de corte"
          value={
            orden.costo_corte > 0 ? formatCLP(orden.costo_corte) : "—"
          }
        />
        <Card
          label="Costo reposición"
          value={
            orden.costo_reposicion > 0 ? formatCLP(orden.costo_reposicion) : "—"
          }
        />
      </section>

      <section className="bg-white rounded-lg border border-neutral-200">
        <header className="px-4 py-3 border-b border-neutral-100">
          <h3 className="text-sm font-semibold text-neutral-900">
            Línea de tiempo
          </h3>
        </header>
        <div className="p-4 space-y-1 text-sm">
          <Hito
            label="Generada"
            valor={formatDateTime(orden.fecha_generacion)}
            done
          />
          <Hito
            label="Notificada al socio"
            valor={
              orden.fecha_notificacion
                ? `${fechaLarga(orden.fecha_notificacion)} · N° ${
                    orden.numero_notificacion ?? "sin número"
                  }`
                : "—"
            }
            done={orden.fecha_notificacion !== null}
          />
          <Hito
            label="Corte ejecutado"
            valor={
              orden.fecha_corte_realizada
                ? formatDateTime(orden.fecha_corte_realizada)
                : "—"
            }
            done={orden.fecha_corte_realizada !== null}
          />
          <Hito
            label="Reposición"
            valor={
              orden.fecha_reposicion
                ? formatDateTime(orden.fecha_reposicion)
                : "—"
            }
            done={orden.fecha_reposicion !== null}
          />
        </div>
      </section>

      <section className="bg-white rounded-lg border border-neutral-200 p-4 text-sm">
        <h3 className="text-sm font-semibold text-neutral-900 mb-2">
          Información legal
        </h3>
        <p className="text-neutral-700">{orden.articulo_ley}</p>
        {orden.observaciones && (
          <p className="mt-2 text-neutral-600 italic">"{orden.observaciones}"</p>
        )}
      </section>
    </>
  );
}

function DescargarAviso({ orden }: { orden: OrdenCorteRead }): JSX.Element {
  const toast = useToast();
  const [descargando, setDescargando] = useState(false);
  return (
    <Button
      variant="secondary"
      size="sm"
      icon={FileDown}
      disabled={descargando}
      title="PDF para entregar al socio, con su deuda, la fecha de corte y un recibí conforme"
      onClick={async () => {
        setDescargando(true);
        try {
          await descargarAvisoCorte(orden.id);
        } catch (err) {
          toast.error(err instanceof ApiError ? err.message : "No se pudo generar el aviso");
        } finally {
          setDescargando(false);
        }
      }}
    >
      {descargando ? "Generando…" : "Descargar aviso"}
    </Button>
  );
}

function PanelAcciones({
  orden,
  acciones,
}: {
  orden: OrdenCorteRead;
  acciones: AccionPanel[];
}): JSX.Element | null {
  const [accionAbierta, setAccionAbierta] = useState<AccionPanel["kind"] | null>(
    null,
  );

  if (acciones.length === 0) {
    return (
      <div className="bg-neutral-50 rounded-lg border border-neutral-200 p-4 text-sm text-neutral-600">
        Estado terminal — sin acciones disponibles.
      </div>
    );
  }

  return (
    <section className="bg-white rounded-lg border border-neutral-200 p-4">
      <h3 className="text-sm font-semibold text-neutral-900">Próximo paso</h3>
      {EXPLICACION_POR_ESTADO[orden.estado] && (
        <p className="mt-1 text-sm text-neutral-600">
          {EXPLICACION_POR_ESTADO[orden.estado]}
        </p>
      )}
      <div className="mt-3 flex flex-wrap items-center gap-x-4 gap-y-2">
        {acciones.map((a) => {
          const Icon = ICONS[a.kind];
          const isOpen = accionAbierta === a.kind;
          const toggle = (): void => setAccionAbierta(isOpen ? null : a.kind);
          // Anular es una salida lateral, no el paso siguiente: va como
          // enlace de peligro para no competir con la acción principal.
          if (a.kind === "Anular") {
            return (
              <Button
                key={a.kind}
                variant="linkDanger"
                icon={Icon}
                onClick={toggle}
                aria-expanded={isOpen}
              >
                Anular orden
              </Button>
            );
          }
          return (
            <Button
              key={a.kind}
              variant={a.kind === "Cortar" ? "destructive" : "primary"}
              icon={Icon}
              onClick={toggle}
              aria-expanded={isOpen}
              className={isOpen ? "ring-2 ring-brand-400 ring-offset-1" : undefined}
            >
              {ETIQUETA_ACCION[a.kind]}
            </Button>
          );
        })}
      </div>

      {accionAbierta === "Notificar" && (
        <FormularioNotificar
          orden={orden}
          onClose={() => setAccionAbierta(null)}
        />
      )}
      {accionAbierta === "EnRuta" && (
        <FormularioConfirmacion
          orden={orden}
          accion="EnRuta"
          mensaje="Confirmar que el operador salió a ejecutar el corte"
          onClose={() => setAccionAbierta(null)}
        />
      )}
      {accionAbierta === "Cortar" && (
        <FormularioCortar
          orden={orden}
          onClose={() => setAccionAbierta(null)}
        />
      )}
      {accionAbierta === "Reponer" && (
        <FormularioReponer
          orden={orden}
          onClose={() => setAccionAbierta(null)}
        />
      )}
      {accionAbierta === "Anular" && (
        <FormularioAnular
          orden={orden}
          onClose={() => setAccionAbierta(null)}
        />
      )}
    </section>
  );
}

function useTransicion<TPayload>(
  orden: OrdenCorteRead,
  fn: (id: number, p: TPayload) => Promise<unknown>,
  onDone: () => void,
  successLabel: string,
) {
  const qc = useQueryClient();
  const toast = useToast();
  const [error, setError] = useState<string | null>(null);
  const m = useMutation({
    mutationFn: (p: TPayload) => fn(orden.id, p),
    onSuccess: () => {
      toast.success(successLabel);
      void qc.invalidateQueries({ queryKey: ["orden-corte", orden.id] });
      void qc.invalidateQueries({ queryKey: ["ordenes-corte"] });
      void qc.invalidateQueries({ queryKey: ["contratos"] });
      onDone();
    },
    onError: (err) => {
      const msg = err instanceof ApiError ? err.message : "Error en transición";
      setError(msg);
      toast.error(msg);
    },
  });
  return { error, mutation: m, setError };
}

function FormularioNotificar({
  orden,
  onClose,
}: {
  orden: OrdenCorteRead;
  onClose: () => void;
}): JSX.Element {
  const hoy = hoyLocalISO();
  const [fecha, setFecha] = useState(hoy);
  const { error, mutation, setError } = useTransicion(
    orden,
    (id, p: { fecha_notificacion: string }) => notificarOrden(id, p),
    onClose,
    "Notificación registrada",
  );

  return (
    <div className="mt-3 rounded-md border border-neutral-200 bg-neutral-50 p-3 space-y-2">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
        <Input
          label="Fecha de notificación"
          type="date"
          max={hoy}
          value={fecha}
          onChange={(e) => setFecha(e.target.value)}
        />
      </div>
      <p className="text-xs text-neutral-600">
        Al confirmar, la orden recibe su N° de notificación. Después, con
        "Descargar aviso" (arriba) imprimes el aviso con ese número para dejarlo
        en la casa con el recibí conforme.
      </p>
      {error && <p className="text-xs text-rose-700">{error}</p>}
      <div className="flex justify-end gap-2">
        <button
          type="button"
          onClick={onClose}
          className="px-3 py-1 text-sm text-neutral-600 hover:text-neutral-900"
        >
          Cancelar
        </button>
        <button
          type="button"
          onClick={() => {
            setError(null);
            if (!fecha || fecha > hoy) {
              setError("La fecha de notificación no puede ser futura");
              return;
            }
            mutation.mutate({ fecha_notificacion: fecha });
          }}
          disabled={mutation.isPending}
          className="rounded-md bg-brand-700 px-3 py-1 text-sm text-white hover:bg-brand-800 disabled:opacity-50"
        >
          {mutation.isPending ? "Notificando…" : "Confirmar notificación"}
        </button>
      </div>
    </div>
  );
}

function FormularioConfirmacion({
  orden,
  accion,
  mensaje,
  onClose,
}: {
  orden: OrdenCorteRead;
  accion: "EnRuta";
  mensaje: string;
  onClose: () => void;
}): JSX.Element {
  const { error, mutation, setError } = useTransicion(
    orden,
    (id) => marcarEnRuta(id),
    onClose,
    "Operador en ruta",
  );
  // accion explícito por si en el futuro agregamos más confirmaciones simples
  void accion;

  return (
    <div className="mt-3 rounded-md border border-neutral-200 bg-neutral-50 p-3 space-y-2">
      <p className="text-sm text-neutral-700">{mensaje}</p>
      {error && <p className="text-xs text-rose-700">{error}</p>}
      <div className="flex justify-end gap-2">
        <button
          type="button"
          onClick={onClose}
          className="px-3 py-1 text-sm text-neutral-600 hover:text-neutral-900"
        >
          Cancelar
        </button>
        <button
          type="button"
          onClick={() => {
            setError(null);
            mutation.mutate(undefined as never);
          }}
          disabled={mutation.isPending}
          className="rounded-md bg-brand-700 px-3 py-1 text-sm text-white hover:bg-brand-800 disabled:opacity-50"
        >
          {mutation.isPending ? "Procesando…" : "Confirmar"}
        </button>
      </div>
    </div>
  );
}

function FormularioCortar({
  orden,
  onClose,
}: {
  orden: OrdenCorteRead;
  onClose: () => void;
}): JSX.Element {
  // Pre-llenado con el cargo configurado por el comité; editable caso a caso.
  const cfgQ = useQuery({
    queryKey: ["empresa-configuracion"],
    queryFn: fetchEmpresaConfiguracion,
  });
  const [costoEditado, setCosto] = useState<string | null>(null);
  const costo = costoEditado ?? String(cfgQ.data?.cargo_corte ?? 0);
  const { error, mutation, setError } = useTransicion(
    orden,
    (id, p: { costo_corte: number }) => cortarOrden(id, p),
    onClose,
    "Corte ejecutado · contrato pasa a Cortado",
  );

  return (
    <div className="mt-3 rounded-md border border-rose-200 bg-rose-50 p-3 space-y-2">
      <p className="flex items-start gap-1.5 text-sm text-rose-900">
        <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" strokeWidth={1.5} aria-hidden="true" />
        <span>
          Esto marca el contrato como <strong>Cortado</strong>. El suministro
          queda suspendido hasta una reposición explícita.
        </span>
      </p>
      <Input
        label="Costo administrativo del corte (CLP)"
        type="number"
        min={0}
        value={costo}
        onChange={(e) => setCosto(e.target.value)}
        className="w-32"
      />
      {error && <p className="text-xs text-rose-700">{error}</p>}
      <div className="flex justify-end gap-2">
        <button
          type="button"
          onClick={onClose}
          className="px-3 py-1 text-sm text-neutral-600 hover:text-neutral-900"
        >
          Cancelar
        </button>
        <button
          type="button"
          onClick={() => {
            setError(null);
            const c = Number(costo);
            if (!Number.isFinite(c) || c < 0) {
              setError("Costo inválido");
              return;
            }
            mutation.mutate({ costo_corte: c });
          }}
          disabled={mutation.isPending}
          className="rounded-md bg-rose-600 px-3 py-1 text-sm text-white hover:bg-rose-700 disabled:opacity-50"
        >
          {mutation.isPending ? "Cortando…" : "Ejecutar corte"}
        </button>
      </div>
    </div>
  );
}

function FormularioReponer({
  orden,
  onClose,
}: {
  orden: OrdenCorteRead;
  onClose: () => void;
}): JSX.Element {
  // Pre-llenado con el cargo configurado por el comité; editable caso a caso.
  const cfgQ = useQuery({
    queryKey: ["empresa-configuracion"],
    queryFn: fetchEmpresaConfiguracion,
  });
  const [costoEditado, setCosto] = useState<string | null>(null);
  const costo = costoEditado ?? String(cfgQ.data?.cargo_reposicion ?? 0);
  // Recorrido M6: reponer con deuda es decisión del comité, pero se ve y se
  // justifica. La deuda es la de esta casa, no todo lo que deba la persona.
  const deudaQ = useQuery({
    queryKey: ["contrato", orden.contrato_id, "deuda"],
    queryFn: () => deudaDelContrato(orden.contrato_id),
  });
  const deuda = deudaQ.data?.deuda ?? 0;
  const [observacion, setObservacion] = useState("");
  const { error, mutation, setError } = useTransicion(
    orden,
    (id, p: { costo_reposicion: number; observacion: string | null }) =>
      reponerOrden(id, p),
    onClose,
    "Suministro repuesto · contrato vuelve a Activo",
  );

  return (
    <div className="mt-3 rounded-md border border-emerald-200 bg-emerald-50 p-3 space-y-2">
      <p className="text-sm text-emerald-900">
        El contrato vuelve a <strong>Activo</strong> y el suministro se restablece.
      </p>
      {deudaQ.isPending ? (
        <p className="text-xs text-neutral-500">Consultando la deuda…</p>
      ) : deuda > 0 ? (
        <div className="rounded-md border border-amber-300 bg-amber-50 p-2 text-sm text-amber-900">
          Esta casa todavía debe <strong>{formatCLP(deuda)}</strong>. Se puede
          reponer igual, pero hay que dejar escrito por qué.
        </div>
      ) : (
        <p className="text-xs text-emerald-800">Esta casa no tiene deuda pendiente.</p>
      )}
      <Input
        label="Costo de reposición (CLP)"
        type="number"
        min={0}
        value={costo}
        onChange={(e) => setCosto(e.target.value)}
        className="w-32"
      />
      {deuda > 0 && (
        <Input
          label="Por qué se repone con deuda (queda en la bitácora)"
          type="text"
          maxLength={255}
          value={observacion}
          onChange={(e) => setObservacion(e.target.value)}
          placeholder="ej. Convenio de pago acordado con el directorio"
        />
      )}
      {error && <p className="text-xs text-rose-700">{error}</p>}
      <div className="flex justify-end gap-2">
        <button
          type="button"
          onClick={onClose}
          className="px-3 py-1 text-sm text-neutral-600 hover:text-neutral-900"
        >
          Cancelar
        </button>
        <button
          type="button"
          onClick={() => {
            setError(null);
            const c = Number(costo);
            if (!Number.isFinite(c) || c < 0) {
              setError("Costo inválido");
              return;
            }
            if (deuda > 0 && observacion.trim().length < 3) {
              setError("Explica por qué se repone con deuda");
              return;
            }
            mutation.mutate({
              costo_reposicion: c,
              observacion: observacion.trim() || null,
            });
          }}
          disabled={mutation.isPending}
          className="rounded-md bg-emerald-600 px-3 py-1 text-sm text-white hover:bg-emerald-700 disabled:opacity-50"
        >
          {mutation.isPending ? "Reponiendo…" : "Confirmar reposición"}
        </button>
      </div>
    </div>
  );
}

function FormularioAnular({
  orden,
  onClose,
}: {
  orden: OrdenCorteRead;
  onClose: () => void;
}): JSX.Element {
  const [motivo, setMotivo] = useState("");
  // Recorrido M8: anular una orden Cortada deja el contrato cortado y sin
  // orden vigente. Se pide confirmarlo explícitamente.
  const yaCortada = orden.estado === "Cortada";
  const [entiendo, setEntiendo] = useState(false);
  const { error, mutation, setError } = useTransicion(
    orden,
    (id, p: { motivo: string }) => anularOrden(id, p.motivo),
    onClose,
    "Orden anulada",
  );

  return (
    <div className="mt-3 rounded-md border border-rose-200 bg-rose-50 p-3 space-y-2">
      {yaCortada ? (
        <div className="rounded-md border border-rose-300 bg-white p-2 text-sm text-rose-900">
          <p>
            <strong>El corte ya se ejecutó.</strong> Anular la orden no
            restablece el servicio: el contrato queda <strong>Cortado</strong>{" "}
            y sin ninguna orden que lo explique. Si hay que dar el agua, usa
            <strong> Reponer</strong> en vez de anular.
          </p>
          <label className="mt-2 flex items-start gap-2 text-xs">
            <input
              type="checkbox"
              className="mt-0.5"
              checked={entiendo}
              onChange={(e) => setEntiendo(e.target.checked)}
            />
            Entiendo que el socio sigue cortado
          </label>
        </div>
      ) : (
        <p className="text-xs text-rose-900">
          Anular preserva la auditoría. El contrato no se toca.
        </p>
      )}
      <Input
        label="Motivo (mín 3 caracteres)"
        type="text"
        maxLength={255}
        value={motivo}
        onChange={(e) => setMotivo(e.target.value)}
        placeholder="ej. Socio pagó toda la deuda antes del corte"
      />
      {error && <p className="text-xs text-rose-700">{error}</p>}
      <div className="flex justify-end gap-2">
        <button
          type="button"
          onClick={onClose}
          className="px-3 py-1 text-sm text-neutral-600 hover:text-neutral-900"
        >
          Cancelar
        </button>
        <button
          type="button"
          onClick={() => {
            setError(null);
            if (motivo.trim().length < 3) {
              setError("Motivo requerido");
              return;
            }
            if (yaCortada && !entiendo) {
              setError("Confirma que el socio sigue cortado");
              return;
            }
            mutation.mutate({ motivo: motivo.trim() });
          }}
          disabled={mutation.isPending}
          className="rounded-md bg-rose-600 px-3 py-1 text-sm text-white hover:bg-rose-700 disabled:opacity-50"
        >
          {mutation.isPending ? "Anulando…" : "Anular orden"}
        </button>
      </div>
    </div>
  );
}

function Card({ label, value }: { label: string; value: string }): JSX.Element {
  return (
    <div className="rounded-lg border bg-white border-neutral-200 p-4">
      <div className="text-xs uppercase tracking-wider text-neutral-500">
        {label}
      </div>
      <div className="mt-1 text-lg font-semibold text-neutral-900">{value}</div>
    </div>
  );
}

function Hito({
  label,
  valor,
  done,
}: {
  label: string;
  valor: string;
  done: boolean;
}): JSX.Element {
  return (
    <div className="flex items-baseline gap-3">
      <span
        className={`inline-block h-2 w-2 rounded-full ${
          done ? "bg-emerald-500" : "bg-neutral-300"
        }`}
      />
      <span className={done ? "text-neutral-900" : "text-neutral-500"}>{label}</span>
      <span className="ml-auto text-neutral-600">{valor}</span>
    </div>
  );
}
